export default class Accordion {
    constructor(container) {
        if (typeof container === 'string') {
            const found = document.querySelector(container);
            if (!found) {
                console.error('Accordion container not found:', container);
                return;
            }
            this.container = found;
        }
        else {
            this.container = container;
        }
        this.accordionItems = Array.from(this.container.querySelectorAll('.accordion-item'));
        this.resizeHandler = this.handleResize.bind(this);
        this.initAccordions();
        this.bindResizeEvent();
    }
    initAccordions() {
        this.accordionItems.forEach((item) => {
            const button = item.querySelector('.accordion-header');
            const content = item.querySelector('.accordion-content');
            if (!button || !content)
                return;
            // init content state
            if (item.classList.contains('is-open')) {
                content.style.height = 'auto';
                content.setAttribute('aria-hidden', 'false');
                button.setAttribute('aria-expanded', 'true');
                content.setAttribute('aria-live', 'polite');
                this.makeContentElementsTabbable(content);
            }
            else {
                content.style.height = '0px';
                content.setAttribute('aria-hidden', 'true');
                button.setAttribute('aria-expanded', 'false');
                content.setAttribute('aria-live', 'off');
                this.makeContentElementsUntabbable(content);
            }
            // add click event
            button.addEventListener('click', () => {
                this.toggleAccordion(item);
            });
            // add keyboard events to button
            button.addEventListener('keydown', (e) => {
                this.handleButtonKeydown(e, item);
            });
        });
    }
    toggleAccordion(item) {
        const button = item.querySelector('.accordion-header');
        const content = item.querySelector('.accordion-content');
        if (!button || !content)
            return;
        const isOpening = !item.classList.contains('is-open');
        if (isOpening) {
            // close other accordions if needed
            // this.closeAllAccordionsExcept(item);
            // open accordion
            this.openAccordion(item, button, content);
        }
        else {
            // close accordion
            this.closeAccordion(item, button, content);
        }
    }
    openAccordion(item, button, content) {
        item.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        content.setAttribute('aria-hidden', 'false');
        content.setAttribute('aria-live', 'polite');
        // make all interactive elements inside content tabbable
        this.makeContentElementsTabbable(content);
        // animate height
        content.style.height = 'auto';
        const startHeight = content.offsetHeight;
        content.style.height = '0px';
        // force reflow
        content.offsetHeight;
        requestAnimationFrame(() => {
            content.style.height = `${startHeight}px`;
            // after transition, set to auto for dynamic content
            const transitionEndHandler = () => {
                if (content.style.height !== '0px') {
                    content.style.height = 'auto';
                }
                content.removeEventListener('transitionend', transitionEndHandler);
            };
            content.addEventListener('transitionend', transitionEndHandler);
        });
    }
    closeAccordion(item, button, content) {
        button.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');
        content.setAttribute('aria-live', 'off');
        // make all interactive elements inside content not tabbable
        this.makeContentElementsUntabbable(content);
        // store current height and animate to 0
        const startHeight = content.offsetHeight;
        content.style.height = `${startHeight}px`;
        // force reflow
        content.offsetHeight;
        requestAnimationFrame(() => {
            content.style.height = '0px';
            const transitionEndHandler = () => {
                item.classList.remove('is-open');
                content.removeEventListener('transitionend', transitionEndHandler);
            };
            content.addEventListener('transitionend', transitionEndHandler);
        });
    }
    closeAllAccordionsExcept(exceptItem) {
        this.accordionItems.forEach((item) => {
            if (item !== exceptItem && item.classList.contains('is-open')) {
                const button = item.querySelector('.accordion-header');
                const content = item.querySelector('.accordion-content');
                if (button && content) {
                    this.closeAccordion(item, button, content);
                }
            }
        });
    }
    makeContentElementsTabbable(container) {
        // find all interactive elements that should be tabbable
        const tabbableElements = container.querySelectorAll('a[href], button');
        tabbableElements.forEach((element) => {
            // remove tabindex="-1"
            if (element.getAttribute('tabindex') === '-1') {
                element.removeAttribute('tabindex');
            }
        });
    }
    makeContentElementsUntabbable(container) {
        // find all interactive elements that should not be tabbable when closed
        const tabbableElements = container.querySelectorAll('a[href], button');
        tabbableElements.forEach((element) => {
            // add tabindex="-1" to make them untabbable
            element.setAttribute('tabindex', '-1');
        });
    }
    handleButtonKeydown(e, item) {
        const button = e.currentTarget;
        const content = item.querySelector('.accordion-content');
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                this.toggleAccordion(item);
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (item.classList.contains('is-open') && content) {
                    // focus first focusable element in content
                    const firstFocusable = this.getFirstFocusableElement(content);
                    if (firstFocusable) {
                        firstFocusable.focus();
                    }
                }
                else {
                    this.focusNextAccordion(item);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (item.classList.contains('is-open') && content) {
                    // if user in content and presses arrowup - go back to button
                    if (content.contains(document.activeElement)) {
                        button.focus();
                    }
                    else {
                        this.focusPreviousAccordion(item);
                    }
                }
                else {
                    this.focusPreviousAccordion(item);
                }
                break;
            case 'Home':
                e.preventDefault();
                this.focusFirstAccordion();
                break;
            case 'End':
                e.preventDefault();
                this.focusLastAccordion();
                break;
            // tab key
            default:
                break;
        }
    }
    getFocusableElements(container) {
        return Array.from(container.querySelectorAll('a[href], button'));
    }
    getFirstFocusableElement(container) {
        const focusable = this.getFocusableElements(container);
        return focusable.length > 0 ? focusable[0] : null;
    }
    focusNextAccordion(currentItem) {
        const currentIndex = this.accordionItems.indexOf(currentItem);
        if (currentIndex < this.accordionItems.length - 1) {
            const nextButton = this.accordionItems[currentIndex + 1].querySelector('.accordion-header');
            nextButton === null || nextButton === void 0 ? void 0 : nextButton.focus();
        }
    }
    focusPreviousAccordion(currentItem) {
        const currentIndex = this.accordionItems.indexOf(currentItem);
        if (currentIndex > 0) {
            const prevButton = this.accordionItems[currentIndex - 1].querySelector('.accordion-header');
            prevButton === null || prevButton === void 0 ? void 0 : prevButton.focus();
        }
    }
    focusFirstAccordion() {
        var _a;
        const firstButton = (_a = this.accordionItems[0]) === null || _a === void 0 ? void 0 : _a.querySelector('.accordion-header');
        firstButton === null || firstButton === void 0 ? void 0 : firstButton.focus();
    }
    focusLastAccordion() {
        const lastItem = this.accordionItems[this.accordionItems.length - 1];
        const lastButton = lastItem === null || lastItem === void 0 ? void 0 : lastItem.querySelector('.accordion-header');
        lastButton === null || lastButton === void 0 ? void 0 : lastButton.focus();
    }
    bindResizeEvent() {
        window.addEventListener('resize', this.resizeHandler);
    }
    handleResize() {
        this.accordionItems.forEach((item) => {
            const content = item.querySelector('.accordion-content');
            if (item.classList.contains('is-open') && content) {
                // set height to auto - get new content height
                content.style.height = 'auto';
                const newHeight = content.offsetHeight;
                content.style.height = `${newHeight}px`;
            }
        });
    }
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        // remove event listeners
        this.accordionItems.forEach((item) => {
            const button = item.querySelector('.accordion-header');
            const content = item.querySelector('.accordion-content');
            if (button) {
                button.replaceWith(button.cloneNode(true));
            }
            if (content) {
                content.replaceWith(content.cloneNode(true));
            }
        });
    }
}
